// Edición IA de los 13 básicos con el modelo seleccionado (tarea «ajustes»).
// El balance de blancos viaja RELATIVO a «Como se tomó» (temperature_shift/tint_shift): el
// plugin lo resuelve con la temperatura real de cada foto. Nunca un Kelvin absoluto inventado.
import { NEUTRAL_TEMP } from "@/lib/rawaistudio/whiteBalance";

export const AI_EDIT_BATCH = 4;
export const AI_EDIT_CONCURRENCY = 2;
const BASIC_KEYS = ["exposure", "contrast", "highlights", "shadows", "whites", "blacks", "texture", "clarity", "dehaze", "vibrance", "saturation"];

// Motor local (respaldo) → mismo formato relativo.
export function engineToRelative(adj) {
  const a = adj || {};
  const out = {};
  for (const k of BASIC_KEYS) out[k] = Number.isFinite(a[k]) ? a[k] : 0;
  out.temperature = null;
  out.tint = null;
  out.temperature_shift = Number.isFinite(a.temperature) ? Math.round(a.temperature - NEUTRAL_TEMP) : 0;
  out.tint_shift = Number.isFinite(a.tint) && Number.isFinite(a.temperature) ? Math.round(a.tint) : 0;
  return out;
}

// Básicos finales de una foto: IA si respondió; si no, motor local.
export function payloadBasics(p, engineAdj) {
  const ai = p.aiEdit?.basics;
  let out;
  if (!ai) out = engineToRelative(engineAdj);
  else {
    out = { temperature: null, tint: null };
    for (const k of [...BASIC_KEYS, "temperature_shift", "tint_shift"]) out[k] = Number.isFinite(ai[k]) ? ai[k] : 0;
  }
  // «Como se tomó» leído del RAW (DNG) → el XMP del ZIP lleva el Kelvin final ya resuelto.
  out.as_shot_temperature = p.asShot?.temperature ?? null;
  out.as_shot_tint = p.asShot?.tint ?? null;
  return out;
}

const NON_RAW = /\.(jpe?g|tiff?|png|heic|heif|webp)$/i;
// Cómo viaja el WB en el XMP/ZIP: "absolute" | "incremental" | "plugin_only" | "none".
export function zipWbMode(p, basics) {
  if (!(basics?.temperature_shift || basics?.tint_shift)) return "none";
  if (NON_RAW.test(p.name || "")) return "incremental";
  return Number.isFinite(p.asShot?.temperature) ? "absolute" : "plugin_only";
}

const r2 = (v) => (Number.isFinite(v) ? Math.round(v * 100) / 100 : undefined);
export function metricsSummary(p) {
  const pm = p.photometrics || {};
  const c = p.color || {};
  const out = {
    mean_luma: r2((pm.p50 ?? NaN) / 255), subject_luma: r2((pm.cP50 ?? NaN) / 255),
    clip_high: r2(pm.highClip), clip_low: r2(pm.lowClip), faces: p.faceCount || 0,
    skin_detected: (c.skinRatio || 0) > 0.006,
    cast: c.meanR ? `R${Math.round(c.meanR)} G${Math.round(c.meanG)} B${Math.round(c.meanB)}` : undefined,
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined));
}

function chunks(arr, n) { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; }
const strip = (b) => String(b || "").replace(/^data:image\/[a-z+]+;base64,/, "");
const FATAL = new Set([400, 401, 402, 403]);

// photos: objetos de foto (se mutan con aiEdit / aiEditError). post(url, body) → { data }.
// getImage(p) → base64 JPEG (≤ ~800 px). Devuelve { edited, fallback, errors, fatal }.
export async function runAiEdit(photos, { post, getImage, onProgress, batch = AI_EDIT_BATCH, concurrency = AI_EDIT_CONCURRENCY }) {
  const todo = photos.filter((p) => !p.aiEdit?.basics);
  const groups = chunks(todo, batch);
  let done = photos.length - todo.length;
  let fatal = null;
  const errors = [];
  onProgress?.(done, photos.length);
  let next = 0;
  const worker = async () => {
    while (next < groups.length && !fatal) {
      const group = groups[next++];
      try {
        const items = [];
        for (const p of group) {
          const img = strip(await getImage(p));
          if (img) items.push({ p, body: { filename: p.name, image_base64: img, metrics: metricsSummary(p) } });
          else p.aiEditError = "sin vista previa para la IA";
        }
        if (items.length) {
          const { data } = await post("/ai/edit-basics", { photos: items.map((i) => i.body) });
          for (const { p } of items) {
            const r = data?.results?.[p.name];
            if (r?.basics) { p.aiEdit = { ...r, provider: data.provider, model: data.model }; p.aiEditError = null; }
            else p.aiEditError = "el modelo no devolvió ajustes para esta foto";
          }
        }
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || "error del proveedor";
        group.forEach((p) => { p.aiEditError = `${status ? `Error ${status}: ` : ""}${msg}`; });
        errors.push(`${status || "red"}: ${msg}`);
        if (FATAL.has(status)) fatal = `Error ${status}: ${msg}`;
      }
      done += group.length;
      onProgress?.(Math.min(done, photos.length), photos.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, groups.length) }, worker));
  const edited = photos.filter((p) => p.aiEdit?.basics).length;
  return { edited, fallback: photos.length - edited, errors: Array.from(new Set(errors)), fatal };
}
